import React from "react";
import styled from "styled-components";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CloseIcon from "@material-ui/icons/Close";
import { Moment } from "moment";
import momentTz from "moment-timezone";
import {
  DateTimePicker,
  FooterSection,
} from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { useAppDispatch, useAppSelector } from "core/store";
import { setDateEndAt, setDateStartAt } from "reports/slice";

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const DateText = styled.div`
  color: ${colors.gray1};
  flex: 1;
  text-align: start;
`;

const PlaceholderText = styled.div`
  color: ${colors.gray6};
  flex: 1;
  text-align: start;
`;

const StyledCalendarIcon = styled(CalendarTodayIcon)`
  color: ${colors.gray1};
  font-size: 1.714rem;
`;

const StyledCloseIcon = styled(CloseIcon)`
  color: ${colors.gray1};
  font-size: 1.714rem;
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  & ${FooterSection} {
    display: none;
  }
`;

const Text = styled.span`
  color: ${colors.gray1};
  margin: 0 0.571rem;
`;

const Trigger = styled(Button)`
  align-items: center;
  justify-content: space-between;
  padding: 0;
  width: 11rem;
`;

interface DatePickerProps {
  isStart: boolean;
}

const DatePickerDropdown = ({ isStart }: DatePickerProps) => {
  const dateStartAt = useAppSelector(state => state.reports.dateStartAt);
  const dateEndAt = useAppSelector(state => state.reports.dateEndAt);
  const dispatch = useAppDispatch();
  const { track } = useSegment();
  const timezone = useCurrentUserTimezone();

  const date = isStart ? dateStartAt : dateEndAt;
  const momentDate = date ? momentTz.tz(date, timezone) : null;
  const setterFunction = isStart ? setDateStartAt : setDateEndAt;

  const isOutsideRange = (day: Moment) => {
    if (isStart) {
      if (dateEndAt && day.isAfter(dateEndAt)) {
        return true;
      }

      return day.isAfter(momentTz(timezone).startOf("day"), "day");
    }

    if (dateStartAt && day.isBefore(dateStartAt)) {
      return true;
    }

    return day.isAfter(momentTz(timezone).endOf("day"));
  };

  return (
    <Dropdown
      trigger={
        <Trigger iconLeft iconSize="1.143rem">
          <StyledCalendarIcon fontSize="inherit" />
          {momentDate ? (
            <>
              <DateText>{momentDate.format("MMM D, YYYY")}</DateText>
              <StyledCloseIcon
                fontSize="inherit"
                onClick={e => {
                  e.stopPropagation();
                  dispatch(setterFunction(null));

                  const element = `reports_date_filter.close_icon.${
                    isStart ? "startDate" : "endDate"
                  }`;
                  track({
                    eventName: "clear_report_date_filter",
                    action: "click",
                    element,
                  });
                }}
              />
            </>
          ) : (
            <PlaceholderText>
              {isStart ? "Start Date" : "End Date"}
            </PlaceholderText>
          )}
        </Trigger>
      }
    >
      {({ isOpen, closePortal }) =>
        isOpen && (
          <DropdownContent>
            <StyledDateTimePicker
              date={momentDate}
              isOutsideRange={isOutsideRange}
              onDateChange={nextDate => {
                const nextDateString = isStart
                  ? nextDate.startOf("day").toISOString()
                  : nextDate.endOf("day").toISOString();
                dispatch(setterFunction(nextDateString));
                closePortal();

                const element = `reports_date_filter.date_picker.${
                  isStart ? "startDate" : "endDate"
                }`;
                track({
                  eventName: "set_report_date_filter",
                  action: "click",
                  element,
                  props: {
                    nextDate: nextDateString,
                  },
                });
              }}
            />
          </DropdownContent>
        )
      }
    </Dropdown>
  );
};
export const ReportsDateFilters = () => {
  return (
    <Container>
      <DatePickerDropdown isStart />
      <Text>to</Text>
      <DatePickerDropdown isStart={false} />
    </Container>
  );
};
